import {
  Badge,
  Button,
  Group,
  Stack,
  Tooltip,
  UnstyledButton
} from "@mantine/core";
import { IconDownload, IconPlus } from "@tabler/icons-react";
import type {
  MRT_ColumnDef,
  MRT_TableInstance
} from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo } from "react";
import { IconButton } from "../../../mantine/components/IconButton/IconButton";
import { getDefaultMRTOptions } from "../../../mantine/getDefaultMRTOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { emptyArray, MEDIUM_LABEL_MAP } from "../../../utils/constants";
import { downloadAsExcel } from "../../../utils/files/downloadAsExcel";
import type { PrefillData } from "../CreateEdaVersionModal/CreateEdaVersionModal.types";
import { ORIGIN } from "../CreateEdaVersionModal/CreateEdaVersionModal.types";
import type { ArrayedMBKResponse, Eda, MBKResponse } from "./Mbk";
import { useEnergyDataAcquisitionTags } from "./useEnergyDataAcquisitionTags";

type OnOpenViewEdaModal = (mbk: Eda) => void;

type TableColumnArgs = {
  onClickNewEdaVersion: (
    prefillData?: PrefillData,
    isNewVersion?: boolean
  ) => void;
  onOpenViewEdaModal: OnOpenViewEdaModal;
};

type EdaTableProps = {
  tableData: ArrayedMBKResponse;
  isMieterstromSite?: boolean;
  setViewMieterstromMBKModalIsOpen: (isOpen: boolean) => void;
  isLoading?: boolean;
} & TableColumnArgs;

function EdaTableV2({
  onClickNewEdaVersion,
  tableData,
  onOpenViewEdaModal,
  isMieterstromSite,
  setViewMieterstromMBKModalIsOpen,
  isLoading
}: EdaTableProps) {
  const { data: tags } = useEnergyDataAcquisitionTags();

  const columns: Array<MRT_ColumnDef<MBKResponse>> = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "publicId",
        size: 80
      },
      {
        header: "Art der Energiemenge",
        accessorKey: "originalVersion.identity.displayName",
        Cell: ({ row }) => (
          <UnstyledButton
            fz={13}
            onClick={() => onOpenViewEdaModal(row.original.originalVersion)}
          >
            {row.original.originalVersion.identity.displayName}
          </UnstyledButton>
        ),
        aggregationFn: (_columnId, leafRows) =>
          leafRows[0].original.originalVersion.identity.displayName,
        AggregatedCell: ({ cell }) =>
          cell.getValue<
            MBKResponse["originalVersion"]["identity"]["displayName"]
          >(),
        mantineTableBodyCellProps: ({ row }) =>
          row.getIsGrouped() ? { fw: "bold" } : {},
        minSize: 200,
        size: 300,
        filterVariant: "text",
        filterFn: "includesString",
        enableCellHoverReveal: true
      },
      {
        header: "Referenzen",
        accessorKey: "referenzen",
        Cell: () => <></>,
        aggregationFn: (_columnId, leafRows) => leafRows[0].original.referenzen,
        AggregatedCell: ({ cell }) =>
          cell.getValue<MBKResponse["referenzen"]>(),
        mantineTableBodyCellProps: ({ row }) =>
          row.getIsGrouped() ? { fw: "bold" } : {},
        minSize: 150,
        filterVariant: "text",
        filterFn: "includesString"
      },
      {
        header: "Herkunft",
        accessorKey: "origin",
        accessorFn: (rowData) => ORIGIN[rowData.origin],
        size: 130,
        filterVariant: "multi-select"
      },
      {
        header: "Gültigkeit",
        accessorKey: "timespan",
        filterVariant: "multi-select",
        size: 170
      },
      {
        header: "Medium",
        accessorKey: "medium",
        accessorFn: (rowData) => MEDIUM_LABEL_MAP[rowData.medium],
        size: 120,
        filterVariant: "multi-select"
      },
      {
        header: "Details",
        accessorKey: "details",
        aggregationFn: (_columnId, leafRows) =>
          getCurrentEdaVersionFormula(leafRows.map((row) => row.original)),
        AggregatedCell: ({ cell }) => cell.getValue<string>(),
        size: 305,
        filterVariant: "text",
        filterFn: "includesString",
        enableCellHoverReveal: true
      },
      {
        header: "Ergänzung",
        accessorKey: "customLabel",
        size: 305,
        filterVariant: "text",
        filterFn: "includesString",
        enableCellHoverReveal: true
      },
      {
        header: "Klassifizierung",
        accessorKey: "tags",
        accessorFn: (rowData) =>
          rowData.tags
            ?.map((tag) => tags?.find((t) => t.slug === tag)?.name)
            .join(", "),
        Cell: ({ row }) => (
          <Stack>
            {row.original.tags.map((tag: string) => {
              const tagData = tags?.find((t) => t.slug === tag);
              return (
                <Tooltip key={tag} label={tagData?.long_name}>
                  <Badge autoContrast color={tagData?.background_color}>
                    {tagData?.name}
                  </Badge>
                </Tooltip>
              );
            })}
          </Stack>
        ),
        filterVariant: "text",
        filterFn: "includesString"
      },
      {
        header: "",
        id: "add-new",
        enableSorting: false,
        enableColumnFilter: false,
        minSize: 150,
        aggregationFn: (_columnId, leafRows) =>
          leafRows.map((row) => row.original),
        AggregatedCell: ({ cell }) => {
          const firstRow = cell.getValue<Array<MBKResponse>>()[0];
          return (
            <Button
              variant="default"
              onClick={() => {
                onClickNewEdaVersion(
                  {
                    artDerEnergieMenge: firstRow.type,
                    origin: firstRow.origin,
                    frequency: firstRow.computation?.frequency,
                    medium: firstRow.medium,
                    estimation: firstRow.estimation ?? undefined,
                    originalVersion: firstRow.originalVersion,
                    components: [],
                    connections: []
                  },
                  true
                );
              }}
            >
              + Neue Version
            </Button>
          );
        }
      }
    ],
    [onClickNewEdaVersion, onOpenViewEdaModal, tags]
  );

  function handleClickExport(table: MRT_TableInstance<MBKResponse>) {
    const headers = columns.map((col) => col.header);
    const columnIds = columns.map((col) => col.accessorKey) as Array<
      keyof MBKResponse
    >;

    const allRows = table.getPrePaginationRowModel().rows;

    const leafRows = allRows.flatMap((row) => {
      if (row.getIsGrouped()) {
        return row.subRows ?? [];
      }
      return [row];
    });

    const dataToExport = leafRows.map((row) => {
      const rowData: Record<string, string> = {};
      columnIds.forEach((colId, index) => {
        rowData[headers[index]] = row.getValue(colId);
      });
      return rowData;
    });

    downloadAsExcel(
      [headers, ...dataToExport.map((data) => Object.values(data))],
      "Mess-und-Bilanzierungskonzept"
    );
  }

  const mrtTable = useMantineReactTable<MBKResponse>({
    ...getDefaultMRTOptions({
      emptyRowsFallbackText:
        "Keine Energiedatenerfassungen entsprechen dem gewählten Filter",
      initialState: {
        grouping: ["publicId"],
        sorting: [{ id: "publicId", desc: false }],
        columnSizing: { "mrt-row-expand": 50 }
      },
      hideBorder: true
    }),
    state: {
      isLoading: isLoading ?? false
    },
    ...getMRTTopToolbarCustomActionsRenderer(),
    enableRowSelection: false,
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <Group>
          {isMieterstromSite && (
            <Button
              color="primary"
              onClick={() => setViewMieterstromMBKModalIsOpen(true)}
            >
              Mieterstrom-MBK erzeugen (Basis)
            </Button>
          )}

          <IconButton
            color="primary"
            icon={IconPlus}
            onClick={() => onClickNewEdaVersion()}
          >
            Neue Energiedatenerfassung
          </IconButton>
          <IconButton
            disabled={
              isLoading || table.getPrePaginationRowModel().rows.length === 0
            }
            icon={IconDownload}
            variant="default"
            onClick={() => handleClickExport(table)}
          >
            Als Excel-Datei herunterladen
          </IconButton>
        </Group>
      );
    },
    mantineExpandButtonProps: ({ row }) =>
      row.getIsGrouped() ? {} : { style: { visibility: "hidden" } },
    enableGrouping: true,
    enableColumnResizing: true,
    enableColumnFilters: true,
    columnFilterDisplayMode: "popover",
    enableFacetedValues: true,
    enableFilterMatchHighlighting: false,
    enableColumnDragging: false,
    enablePagination: false,
    columns: columns,
    data: tableData ?? emptyArray
  });

  return <MantineReactTable table={mrtTable} />;
}

function getCurrentEdaVersionFormula(subRows: Array<MBKResponse>) {
  const today = new Date();

  const currentFormula = subRows.reduce((formula, edaVersion) => {
    const startDate = new Date(edaVersion.timespan.split(" - ")[0]);
    const endDate = new Date(edaVersion.timespan.split(" - ")[1]);
    const versionIsCurrentVersion = startDate <= today && today <= endDate;

    return versionIsCurrentVersion ? edaVersion.details : formula;
  }, "");

  return currentFormula;
}

export { EdaTableV2 as EdaTable };
